import { SimpleGrid, Skeleton, Stack, Table, Text } from '@mantine/core'

import {
  percentage_formatter,
  usdAutoFormatter,
} from '@repo/common/helpers/formatters'
import { useGetRedeemableCommissionFee } from '@repo/common/queries/redeem/redeemables'

import { TowerCard } from '@/components/ui/TowerCard'

import classes from './RedeemableFees.module.css'

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {children}
    </Text>
  </Stack>
)

function findCurrentDaysIndex(
  entries: { days_until: number }[],
  days_until_next: number | null | undefined,
): number | null {
  if (days_until_next == null || entries.length === 0) return null

  for (let i = 0; i < entries.length; i++) {
    const next = entries[i + 1]
    if (next == null || days_until_next < next.days_until) {
      return i
    }
  }
  return null
}

function formatDaysHoursMinutes(days: number): string {
  const wholeDays = Math.floor(days)
  const totalMinutes = Math.round((days - wholeDays) * 24 * 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (wholeDays > 0) parts.push(`${wholeDays}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)
  return parts.join(' ')
}

export function RedeemableFees({ redeemableId }: { redeemableId: string }) {
  const { data, isLoading } = useGetRedeemableCommissionFee(redeemableId)

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={100} />
        <Skeleton height={300} />
      </Stack>
    )
  }

  if (!data) {
    return <Text c="dimmed">No fee data available for this redeemable</Text>
  }

  const { commission_fee_info } = data
  const { total_aum, current_unliquidated_total, days_until_next_redemption } =
    commission_fee_info
  const { boundary_percentages, entries } = commission_fee_info.fee_schedule

  const currentEntryIndex = findCurrentDaysIndex(
    entries,
    days_until_next_redemption,
  )

  return (
    <Stack gap="lg" pb="50vh">
      <TowerCard title="Fee Overview">
        <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="lg">
          <Field label="Total AUM">
            {total_aum != null ? usdAutoFormatter(total_aum) : 'N/A'}
          </Field>
          <Field label="Current Unliquidated">
            {current_unliquidated_total != null
              ? usdAutoFormatter(current_unliquidated_total)
              : 'N/A'}
          </Field>
          <Field label="Current R%">
            {total_aum != null && current_unliquidated_total != null
              ? percentage_formatter(
                  (current_unliquidated_total / total_aum) * 100,
                  { maximumFractionDigits: 4 },
                )
              : 'N/A'}
          </Field>
          <Field label="Days Until Next Redemption">
            {days_until_next_redemption != null
              ? formatDaysHoursMinutes(days_until_next_redemption)
              : 'N/A'}
          </Field>
        </SimpleGrid>
      </TowerCard>

      <TowerCard title="Fee Schedule">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Days Until</Table.Th>
              {boundary_percentages.map((bp, i) => (
                <Table.Th key={i} ta="right">
                  ≤{percentage_formatter(bp)}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {entries.map((entry, rowIdx) => (
              <Table.Tr
                key={entry.days_until}
                className={
                  rowIdx === currentEntryIndex ? classes.current_row : undefined
                }
              >
                <Table.Td fw={rowIdx === currentEntryIndex ? 700 : undefined}>
                  {formatDaysHoursMinutes(entry.days_until)}
                </Table.Td>
                {entry.fee_percentages.map((fp, colIdx) => (
                  <Table.Td key={colIdx} ta="right">
                    {percentage_formatter(fp, { maximumFractionDigits: 4 })}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </TowerCard>
    </Stack>
  )
}
